import { useEffect, useRef, useState } from 'react';
import { Common, Widget } from '@stockbit/ui';
import { useDispatch, useSelector } from 'react-redux';

import auth from 'utils/auth';
import { HamburgerButton, HeaderContainer, Input, Tag } from './styled';
import Iconplus from './Iconplus';

import slice, { selectors, effects } from './slice';
import { selectors as watchlistSelectors } from './ListWatchlist/slice';

const { Flex, Text } = Common;
const { AutoSuggestion } = Widget;

interface HeaderProps {
  isAddSymbol?: boolean;
  setAddSymbol?: (params: any) => void;
}

function Header({ isAddSymbol, setAddSymbol }: HeaderProps) {
  // @ts-ignore
  const { watchlist_id } = auth.getUserAccess();

  const { showList, suggestions, watchlistId } = useSelector(selectors);
  const { saved: savedWatchlist } = useSelector(watchlistSelectors);

  const dispatch = useDispatch();

  const [isSearchActive, setSearchActive] = useState<boolean>(false);
  const [timeInterval, setTimeInterval] = useState(0);

  const inputContainerRef = useRef(null);
  const inputRef = useRef(null);
  const [inputVal, setInputVal] = useState('');

  const handleOutsideClick = (e: MouseEvent) => {
    if (
      inputContainerRef.current &&
      !inputContainerRef.current?.contains(e.target)
    ) {
      setSearchActive(false);
      setAddSymbol(false);
    }
  };

  const handleSuggestionClick = (companyid: number | string) => {
    setInputVal('');
    dispatch(effects.saveCompanyToWatchlist({ watchlistId, companyid }));
  };

  const handleSearchCompany = (keyword: string) => {
    if (keyword.length > 1) {
      if (timeInterval) {
        clearTimeout(timeInterval);
      }
      const timeout = window.setTimeout(() => {
        dispatch(effects.getCompanySuggestions({ keyword, page: 1, watchlist_id }));
      }, 500);

      setTimeInterval(timeout);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleOutsideClick);

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (isAddSymbol && !isSearchActive) {
      setSearchActive(true);
    }
  }, [isSearchActive, isAddSymbol]);

  const options = suggestions.map(({ id, name, desc }) => ({
    value: id,
    label: (
      <Flex>
        <Tag>{name}</Tag>
        <Text pl="10px">{desc}</Text>
      </Flex>
    ),
  }));

  const handleWatchlistSwitch = () =>
    dispatch(
      slice.actions.toggleViewType({
        listType: showList === 'company' ? 'saved' : 'company',
      }),
    );

  const onIconplusClick = () => {
    setSearchActive(true);
    inputRef.current.focus();
  };

  const getCurrentWatchlist = () => {
    if (watchlistId) {
      return savedWatchlist.find((item) => item.watchlist_id === watchlistId);
    }

    return null;
  };

  const handlePressEnter = async (e: any) => {
    let val = e;

    if (typeof e !== 'string') {
      val = e.target.value;
    }

    if (val.length > 1) {
      const search = await dispatch(
        effects.getCompanySuggestions({ keyword: val, page: 1, watchlist_id }),
      );

      // @ts-ignore
      const { error, data: searchData } = search.payload;

      if (!error && searchData.length > 0) {
        // Add the first item of array data to watchlist
        const [company] = searchData;

        handleSuggestionClick(company.id);
      }
    }
  };

  const currentWatchlist = getCurrentWatchlist();

  return (
    <HeaderContainer>
      <Text size="16px" family="bold" ellipsis>
        {currentWatchlist?.is_default || !currentWatchlist?.name
          ? 'All Watchlist'
          : currentWatchlist.name}
      </Text>
      <Flex align="center" justify="flex-end" style={{ flex: 1 }}>
        <Flex
          ref={inputContainerRef}
          align="center"
          pr="10px"
          style={{ display: showList === 'saved' ? 'none' : 'flex' }}
        >
          <Iconplus isActive={!isSearchActive} onClick={onIconplusClick} />
          <AutoSuggestion
            options={options}
            value={inputVal}
            onChange={setInputVal}
            style={{ position: 'absolute', left: 0, top: '40px' }}
            onSearch={handleSearchCompany}
            onSelect={handleSuggestionClick}
            getPopupContainer={(trigger) => trigger.parentElement}
          >
            <Input
              ref={inputRef}
              active={isSearchActive}
              placeholder="Search for symbol"
              height="30px"
              onPressEnter={handlePressEnter}
            />
          </AutoSuggestion>
        </Flex>
        <HamburgerButton
          style={{ display: isSearchActive ? 'none' : 'block' }}
          active={showList === 'saved'}
          onClick={handleWatchlistSwitch}
        />
      </Flex>
    </HeaderContainer>
  );
}

export default Header;
